/**
 * Accelerators canvas row (flag `accelerators-row`): the programs the
 * Programs block lists, in display order, and the phases the row derives
 * for each application.
 *
 * Cross-compatible (no "use client"): read by the row service on the server
 * and by the canvas blocks on the client.
 */
import { APP_FORMAT_IDS, type AppFormatId } from "@/constants/accelerators/applications";

export interface AcceleratorRowProgram {
  id: string;
  name: string;
  /**
   * The Accelerator Apps format that holds this program's application. Null
   * only for a program Lev has no format for yet, which lists with Track only
   * and never offers a draft.
   * Whether the program is live comes from the viewer's launch set
   * (src/lib/accelerators/launchedFormats.ts), never from this list.
   */
  formatId: AppFormatId | null;
  /**
   * The program's id in the core catalog (src/constants/programCatalog.ts),
   * or null when the catalog has no entry for it. A constant join key: the
   * row's own `id` is stored in founders' data and never changes with it.
   * Whenever both sides have a format, the catalog entry's `appFormatId`
   * MUST equal this program's `formatId` (src/tests/accelerators.programDirectory.test.ts).
   */
  catalogId: string | null;
  /**
   * While unlaunched for the viewer, left off the row instead of listed with
   * Track only: the row was built for Techstars, so it never advertises
   * Techstars to a viewer outside that flag.
   */
  hideUntilLaunched?: boolean;
}

/** Display order is the founder-facing order of both blocks. */
export const ACCELERATOR_ROW_PROGRAMS: readonly AcceleratorRowProgram[] = [
  {
    id: "techstars",
    name: "Techstars",
    formatId: APP_FORMAT_IDS.TECHSTARS,
    catalogId: "techstars",
    hideUntilLaunched: true,
  },
  { id: "yc", name: "YC", formatId: APP_FORMAT_IDS.YC, catalogId: "y-combinator" },
  {
    id: "plug-and-play",
    name: "Plug and Play",
    formatId: APP_FORMAT_IDS.PLUG_AND_PLAY,
    catalogId: "plug-and-play",
  },
  { id: "speedrun", name: "speedrun", formatId: null, catalogId: "a16z-speedrun" },
];

export const PROGRAMS_BLOCK_TITLE = "Programs";

/**
 * The row's step-family colors: slate blue accent family. The row's blocks
 * and its lightbox header read only these, so the row's color changes here
 * and nowhere else.
 */
export const ACCELERATORS_ROW_COLORS = {
  headerGradientTop: "#D8E7EF",
  headerGradientBottom: "#C2D9E6",
  /** Lightbox headers fade to white, as CanvasLightbox's do; the canvas cards keep `headerGradientBottom`. */
  lightboxHeaderGradientBottom: "#ffffff",
  iconTileColor: "#AFCDDC",
  /** The strong stripe color for lightbox section headers — the role a program's brand primary plays on the Application view. */
  accentBar: "#3A86AD",
} as const;

export function applicationBlockTitle(program: Pick<AcceleratorRowProgram, "name">): string {
  return `${program.name} Application`;
}

export const ACCELERATOR_PHASES = {
  PREPARE: "prepare",
  FINISH: "finish",
  WAITING_FOR_INTERVIEW: "waiting-for-interview",
} as const;

export type AcceleratorPhase = (typeof ACCELERATOR_PHASES)[keyof typeof ACCELERATOR_PHASES];

/** Canvas query param that opens the Accelerators lightbox at a format's application. */
export const ACCELERATOR_ROW_PARAM = "accelerator";

/** Canvas query param that opens the Programs view with one program preselected. */
export const ACCELERATOR_PROGRAMS_PARAM = "programs";

/**
 * What the Accelerators lightbox shows: the Programs view, where the founder
 * confirms what they apply to, or one program's application.
 */
export type AcceleratorsLightboxView =
  | {
      kind: "programs";
      /** Checked when the view opens, if the founder can still pick it. */
      preselectProgramId: string | null;
      /**
       * Every program checked when the view opens, in place of the preselect:
       * a failed confirm reopens the view with the founder's own choice.
       */
      checkedProgramIds?: readonly string[];
    }
  | { kind: "application"; formatId: AppFormatId };

/**
 * The canvas URL that opens the Accelerators row's lightbox. A null format
 * opens the canvas only; callers pass a format already checked against the
 * viewer's launched formats.
 */
export function acceleratorRowHref(projectId: string, format: AppFormatId | null): string {
  return format ? `/${projectId}?${ACCELERATOR_ROW_PARAM}=${format}` : `/${projectId}`;
}

/**
 * A program founder's first stop: the canvas with the Programs view open and
 * their program preselected. `programId` is an `ACCELERATOR_ROW_PROGRAMS` id.
 */
export function programsFirstStopHref(projectId: string, programId: string): string {
  return `/${projectId}?${ACCELERATOR_PROGRAMS_PARAM}=${encodeURIComponent(programId)}`;
}

/**
 * The canvas URL that opens the Accelerators row's Programs view with
 * nothing preselected and no landed program — for a link that points at no
 * program in particular (the signed-in home page's Accelerators
 * announcement banner). An empty `?programs=` value resolves the same as an
 * unlisted program id (preselectProgramId: null), but useAcceleratorsRow
 * additionally treats the empty value as no first-stop landing at all
 * (`landedProgramId` stays null), so it never holds the Big Idea reveal or
 * Lev's welcome waiting on a landing that was never real.
 */
export function programsViewHref(projectId: string): string {
  return `/${projectId}?${ACCELERATOR_PROGRAMS_PARAM}=`;
}
